import { Dayjs } from 'dayjs';
import {
    getSmartScheduleDateKey,
    SmartScheduleCoach,
    SmartScheduleDateInput,
    SmartScheduleEvent,
    SmartSchedulePeriod,
    toSmartScheduleKst,
} from '@/features/smartSchedule/model';

export type SmartTypeFilter = 'all' | 'private' | 'group' | 'other';
export type SmartStatusFilter = 'all' | 'upcoming' | 'ongoing' | 'completed' | 'pending' | 'noshow' | 'off';
export type SmartResolvedStatus = Exclude<SmartStatusFilter, 'all'>;
export type SmartCoachFilterMode = 'all' | 'custom';

export type SmartScheduleFilters = {
    type: SmartTypeFilter;
    status: SmartStatusFilter;
    coachMode: SmartCoachFilterMode;
    coachIds: string[];
    query: string;
};

export type EventLayout = {
    event: SmartScheduleEvent;
    startMinute: number;
    endMinute: number;
    lane: number;
    laneCount: number;
};

export type HiddenOverlap = {
    count: number;
    startMinute: number;
    endMinute: number;
    events: SmartScheduleEvent[];
};

export type OverlapLayout = {
    visible: EventLayout[];
    hidden: HiddenOverlap[];
};

export type WeekSlotEvent = {
    event: SmartScheduleEvent;
    startMinute: number;
    endMinute: number;
};

export type WeekSlotRow = {
    startMinute: number;
    endMinute: number;
    dayEvents: WeekSlotEvent[][];
    maxEventCount: number;
};

export const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

export const DEFAULT_FILTERS: SmartScheduleFilters = {
    type: 'all',
    status: 'all',
    coachMode: 'all',
    coachIds: [],
    query: '',
};

export const startOfMondayWeek = (value: SmartScheduleDateInput) => {
    const date = toSmartScheduleKst(value).startOf('day');
    return date.subtract((date.day() + 6) % 7, 'day');
};

export const getMonthGridDays = (value: SmartScheduleDateInput) => {
    const first = toSmartScheduleKst(value).startOf('month');
    const start = first.subtract((first.day() + 6) % 7, 'day');
    const requiredDays = first.endOf('month').startOf('day').diff(start, 'day') + 1;
    const dayCount = Math.max(35, Math.ceil(requiredDays / 7) * 7);
    return Array.from({ length: dayCount }, (_, index) => start.add(index, 'day'));
};

export const getPeriodRange = (period: SmartSchedulePeriod, selectedDay: SmartScheduleDateInput) => {
    const date = toSmartScheduleKst(selectedDay);
    if (period === 'month') {
        const days = getMonthGridDays(date);
        return { start: days[0].startOf('day'), end: days[days.length - 1].endOf('day') };
    }
    if (period === 'week') {
        const start = startOfMondayWeek(date);
        return { start, end: start.add(6, 'day').endOf('day') };
    }
    return { start: date.startOf('day'), end: date.endOf('day') };
};

export const moveSelectedDay = (
    value: SmartScheduleDateInput,
    period: SmartSchedulePeriod,
    direction: -1 | 1
) => {
    const date = toSmartScheduleKst(value);
    if (period === 'month') return date.date(1).add(direction, 'month');
    if (period === 'week') return date.add(direction, 'week');
    return date.add(direction, 'day');
};

export const getDateKey = (value: SmartScheduleDateInput) => getSmartScheduleDateKey(value);

type CachedEventTiming = {
    startTime: SmartScheduleEvent['startTime'];
    endTime: SmartScheduleEvent['endTime'];
    start: Dayjs;
    end: Dayjs;
};

const eventTimingCache = new WeakMap<SmartScheduleEvent, CachedEventTiming>();

const getEventTiming = (event: SmartScheduleEvent) => {
    const cached = eventTimingCache.get(event);
    if (cached && cached.startTime === event.startTime && cached.endTime === event.endTime) return cached;

    const timing = {
        startTime: event.startTime,
        endTime: event.endTime,
        start: toSmartScheduleKst(event.startTime),
        end: toSmartScheduleKst(event.endTime),
    };
    eventTimingCache.set(event, timing);
    return timing;
};

export const getEventStart = (event: SmartScheduleEvent) => getEventTiming(event).start;
export const getEventEnd = (event: SmartScheduleEvent) => getEventTiming(event).end;

export const eventOverlapsDay = (event: SmartScheduleEvent, date: Dayjs) => {
    const start = getEventStart(event);
    const end = getEventEnd(event);
    return start.isBefore(date.add(1, 'day')) && end.isAfter(date.startOf('day'));
};

export const eventsStartingOn = (events: SmartScheduleEvent[], date: Dayjs) =>
    events
        .filter((event) => getEventStart(event).isSame(date, 'day'))
        .sort((left, right) => getEventStart(left).valueOf() - getEventStart(right).valueOf());

/** Builds the month lookup once instead of scanning every event for every calendar cell. */
export const buildMonthEventIndex = (events: SmartScheduleEvent[]) => {
    const eventsByDate = new Map<string, SmartScheduleEvent[]>();
    events.forEach((event) => {
        const dateKey = getEventStart(event).format('YYYY-MM-DD');
        const dayEvents = eventsByDate.get(dateKey);
        if (dayEvents) dayEvents.push(event);
        else eventsByDate.set(dateKey, [event]);
    });
    eventsByDate.forEach((dayEvents) =>
        dayEvents.sort((left, right) => getEventStart(left).valueOf() - getEventStart(right).valueOf())
    );
    return eventsByDate;
};

const normalizedKind = (event: SmartScheduleEvent) => String(event.kind || '').toLowerCase();
const normalizedStatus = (event: SmartScheduleEvent) => String(event.status || '').toLowerCase();

export const getKindGroup = (event: SmartScheduleEvent): Exclude<SmartTypeFilter, 'all'> => {
    const kind = normalizedKind(event);
    if (kind === 'group' || kind.includes('duet') || kind.includes('1on2') || kind.includes('1on3')) return 'group';
    if (kind === 'private' || kind === 'pt' || kind === 'request' || kind.includes('lesson')) return 'private';
    return 'other';
};

export const getKindLabel = (event: SmartScheduleEvent) => {
    const kind = normalizedKind(event);
    const classType = String(event.classType || '').toUpperCase();
    if (classType === 'PRIVATE_1ON2') return '1:2';
    if (classType === 'PRIVATE_1ON3') return '1:3';
    if (kind === 'group') return '그룹';
    if (kind === 'private' || kind === 'pt') return 'PT';
    if (kind === 'request') return '신청';
    if (kind === 'off' || kind === 'block') return 'OFF';
    if (kind === 'opportunity' || kind === 'newopportunity') return '신규';
    if (kind === 'general') return '일반';
    return '일정';
};

export const getStatusGroup = (
    event: SmartScheduleEvent,
    referenceNow?: Dayjs
): SmartResolvedStatus => {
    const status = normalizedStatus(event);
    if (status.includes('no_show') || status.includes('noshow')) return 'noshow';
    if (status.includes('pending') || status.includes('request') || status.includes('waiting')) return 'pending';
    if (status.includes('off')) return 'off';
    if (status.includes('complete') || status.includes('done') || status.includes('past')) return 'completed';
    const nowValue = referenceNow ? referenceNow.valueOf() : Date.now();
    if (nowValue >= getEventStart(event).valueOf() && nowValue < getEventEnd(event).valueOf()) return 'ongoing';
    if (nowValue >= getEventEnd(event).valueOf()) return 'completed';
    return 'upcoming';
};

export const getStatusLabel = (event: SmartScheduleEvent, resolvedStatus: SmartResolvedStatus = getStatusGroup(event)) => {
    const status = resolvedStatus;
    return {
        upcoming: '예정',
        ongoing: '진행중',
        completed: '완료',
        pending: '신청 대기',
        noshow: '노쇼',
        off: 'OFF',
    }[status];
};

export type CoachColor = Readonly<{
    background: string;
    foreground: string;
    border: string;
}>;

const UNASSIGNED_COACH_COLOR: CoachColor = {
    background: '#EDEFF5',
    foreground: '#46516C',
    border: '#CDD2E0',
};
const coachColorCache = new Map<string, CoachColor>();

/**
 * Spreads similar-looking ids (including sequential database ids) across the
 * full 32-bit range. A small fixed palette made two coaches share an exact
 * color surprisingly often on busy centers.
 */
const hashCoachId = (value: string) => {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    hash ^= hash >>> 16;
    hash = Math.imul(hash, 0x85ebca6b);
    hash ^= hash >>> 13;
    hash = Math.imul(hash, 0xc2b2ae35);
    return (hash ^ (hash >>> 16)) >>> 0;
};

const createCoachColor = (
    hue: number,
    saturation: number,
    backgroundLightness: number,
    foregroundLightness: number,
    borderLightness: number
): CoachColor => ({
    background: `hsl(${Number(hue.toFixed(1))}, ${saturation}%, ${backgroundLightness}%)`,
    foreground: `hsl(${Number(hue.toFixed(1))}, ${Math.min(72, saturation + 5)}%, ${foregroundLightness}%)`,
    border: `hsl(${Number(hue.toFixed(1))}, ${Math.max(42, saturation - 9)}%, ${borderLightness}%)`,
});

export const getCoachColor = (coachId?: string): CoachColor => {
    if (!coachId) return UNASSIGNED_COACH_COLOR;
    const cached = coachColorCache.get(coachId);
    if (cached) return cached;

    const hash = hashCoachId(coachId);
    const hue = (hash % 3600) / 10;
    const saturation = 54 + ((hash >>> 12) % 13);
    const backgroundLightness = 93 + ((hash >>> 21) % 3);
    const foregroundLightness = 28 + ((hash >>> 24) % 5);
    const borderLightness = 78 + ((hash >>> 27) % 6);
    const color = createCoachColor(
        hue,
        saturation,
        backgroundLightness,
        foregroundLightness,
        borderLightness
    );
    coachColorCache.set(coachId, color);
    return color;
};

/**
 * Creates a deterministic, maximally spaced palette for one center roster.
 * Sorting by the id hash makes the result independent from filters and API
 * response order, while distributing 24 coaches at 15-degree hue intervals.
 */
export const buildCoachColorMap = (coachIds: Array<string | undefined>): ReadonlyMap<string, CoachColor> => {
    const stableIds = Array.from(new Set(coachIds.filter((id): id is string => Boolean(id)))).sort((left, right) => {
        const hashDifference = hashCoachId(left) - hashCoachId(right);
        if (hashDifference !== 0) return hashDifference;
        return left < right ? -1 : left > right ? 1 : 0;
    });
    const colorCount = stableIds.length;
    if (colorCount === 0) return new Map();

    return new Map(
        stableIds.map((coachId, index) => {
            const hue = (214 + index * (360 / colorCount)) % 360;
            const saturation = 56 + (index % 3) * 5;
            const backgroundLightness = 92 + (index % 3);
            const foregroundLightness = 27 + (index % 4);
            const borderLightness = 74 + (index % 4) * 2;
            return [
                coachId,
                createCoachColor(
                    hue,
                    saturation,
                    backgroundLightness,
                    foregroundLightness,
                    borderLightness
                ),
            ];
        })
    );
};

export const getEventTone = (event: SmartScheduleEvent) => {
    const kind = normalizedKind(event);
    if (kind === 'group') return 'group';
    if (kind === 'request') return 'request';
    if (kind === 'off' || kind === 'block') return 'off';
    if (kind === 'opportunity' || kind === 'newopportunity') return 'opportunity';
    if (kind === 'general') return 'etc';
    return 'private';
};

export const filterEvents = (
    events: SmartScheduleEvent[],
    filters: SmartScheduleFilters,
    coaches: SmartScheduleCoach[],
    referenceNow?: Dayjs
) => {
    if (filters.coachMode === 'custom' && filters.coachIds.length === 0) return [];
    const queryTokens = filters.query
        .normalize('NFKC')
        .trim()
        .toLocaleLowerCase('ko-KR')
        .split(/\s+/)
        .filter(Boolean);
    if (
        filters.type === 'all' &&
        filters.status === 'all' &&
        filters.coachMode === 'all' &&
        queryTokens.length === 0
    ) {
        return events;
    }
    const knownCoachNames = queryTokens.length > 0
        ? new Map(coaches.map((coach) => [coach.id, coach.name]))
        : null;
    return events.filter((event) => {
        if (filters.type !== 'all' && getKindGroup(event) !== filters.type) return false;
        if (filters.status !== 'all' && getStatusGroup(event, referenceNow) !== filters.status) return false;
        if (filters.coachMode === 'custom' && !filters.coachIds.includes(event.coachId || '')) return false;
        if (queryTokens.length === 0) return true;
        const searchable = [
            event.searchText,
            event.title,
            event.subtitle,
            event.coachName,
            knownCoachNames?.get(event.coachId || ''),
            event.membershipName,
            event.sessionText,
            ...(event.memberNames || []),
        ]
            .filter(Boolean)
            .join(' ')
            .toLocaleLowerCase('ko-KR');
        return queryTokens.every((token) => searchable.includes(token));
    });
};

export const getDisplayHours = (events: SmartScheduleEvent[], days: Dayjs[]) => {
    if (days.length === 0) return { startHour: 6, endHour: 24 };
    const dayStarts = days.map((date) => date.startOf('day'));
    const rangeStart = dayStarts[0];
    const rangeEnd = dayStarts[dayStarts.length - 1].add(1, 'day');
    const scoped = events.filter(
        (event) => getEventStart(event).isBefore(rangeEnd) && getEventEnd(event).isAfter(rangeStart)
    );
    // OFF blocks commonly span midnight and would otherwise force every board
    // into a compressed 24-hour view. Keep their visible portion on the board,
    // but expand only when an actionable schedule exists outside 06:00–24:00.
    const rangeDefiningEvents = scoped.filter((event) => event.kind !== 'off');
    const overlapsEarlyHours = rangeDefiningEvents.some((event) =>
        dayStarts.some((dayStart) => {
            const earlyHoursEnd = dayStart.add(6, 'hour');
            return getEventStart(event).isBefore(earlyHoursEnd) && getEventEnd(event).isAfter(dayStart);
        })
    );
    return {
        startHour: overlapsEarlyHours ? 0 : 6,
        endHour: 24,
    };
};

export const minuteWithinDay = (value: Dayjs, date: Dayjs) => value.diff(date.startOf('day'), 'minute', true);

const compareWeekSlotEvents = (left: WeekSlotEvent, right: WeekSlotEvent) =>
    left.startMinute - right.startMinute
    || left.endMinute - right.endMinute
    || String(left.event.coachId || '').localeCompare(String(right.event.coachId || ''), 'ko-KR')
    || String(left.event.id).localeCompare(String(right.event.id), 'ko-KR');

/** Distributes each event only across the calendar days it actually touches. */
export const buildWeekSlotRows = (
    events: SmartScheduleEvent[],
    days: Dayjs[],
    startMinute: number,
    endMinute: number,
    slotMinutes = 30
): WeekSlotRow[] => {
    const slotCount = Math.max(0, Math.ceil((endMinute - startMinute) / slotMinutes));
    if (slotCount === 0 || days.length === 0) return [];

    const weekStart = days[0].startOf('day');
    const daySlots = days.map(() =>
        Array.from({ length: slotCount }, () => [] as WeekSlotEvent[])
    );

    events.forEach((event) => {
        const eventStart = getEventStart(event);
        const eventEnd = getEventEnd(event);
        if (!eventStart.isValid() || !eventEnd.isValid() || !eventEnd.isAfter(eventStart)) return;

        const firstDayIndex = Math.max(0, eventStart.startOf('day').diff(weekStart, 'day'));
        // End times are exclusive: a session ending exactly at midnight does not
        // create a second card on the following day.
        const lastDayIndex = Math.min(
            days.length - 1,
            eventEnd.subtract(1, 'millisecond').startOf('day').diff(weekStart, 'day')
        );
        if (firstDayIndex > lastDayIndex || lastDayIndex < 0 || firstDayIndex >= days.length) return;

        for (let dayIndex = firstDayIndex; dayIndex <= lastDayIndex; dayIndex += 1) {
            const date = days[dayIndex];
            const visibleStart = Math.max(startMinute, minuteWithinDay(eventStart, date));
            const visibleEnd = Math.min(endMinute, minuteWithinDay(eventEnd, date));
            if (visibleEnd <= visibleStart) continue;

            const slotIndex = Math.min(
                slotCount - 1,
                Math.max(0, Math.floor((visibleStart - startMinute) / slotMinutes))
            );
            daySlots[dayIndex][slotIndex].push({
                event,
                startMinute: visibleStart,
                endMinute: visibleEnd,
            });
        }
    });

    daySlots.forEach((slots) =>
        slots.forEach((slotEvents) => {
            if (slotEvents.length > 1) slotEvents.sort(compareWeekSlotEvents);
        })
    );

    return Array.from({ length: slotCount }, (_, slotIndex) => {
        const dayEvents = daySlots.map((slots) => slots[slotIndex]);
        return {
            startMinute: startMinute + slotIndex * slotMinutes,
            endMinute: Math.min(endMinute, startMinute + (slotIndex + 1) * slotMinutes),
            dayEvents,
            maxEventCount: Math.max(0, ...dayEvents.map((slotEvents) => slotEvents.length)),
        };
    });
};

export const isScheduleSlotFree = (
    events: SmartScheduleEvent[],
    date: Dayjs,
    startMinute: number,
    durationMinutes = 30
) => {
    const slotStart = date.startOf('day').add(startMinute, 'minute');
    const slotEnd = slotStart.add(durationMinutes, 'minute');
    return !events.some(
        (event) => getEventStart(event).isBefore(slotEnd) && getEventEnd(event).isAfter(slotStart)
    );
};

export const layoutOverlaps = (
    events: SmartScheduleEvent[],
    date: Dayjs,
    startHour: number,
    endHour: number,
    maxVisibleLanes = 2
): OverlapLayout => {
    const minMinute = startHour * 60;
    const maxMinute = endHour * 60;
    const intervals = events
        .filter((event) => eventOverlapsDay(event, date))
        .map((event) => ({
            event,
            startMinute: Math.max(minMinute, minuteWithinDay(getEventStart(event), date)),
            endMinute: Math.min(maxMinute, minuteWithinDay(getEventEnd(event), date)),
        }))
        .filter((item) => item.endMinute > item.startMinute)
        .sort((left, right) => left.startMinute - right.startMinute || left.endMinute - right.endMinute);

    const clusters: typeof intervals[] = [];
    let current: typeof intervals = [];
    let clusterEnd = -Infinity;
    intervals.forEach((interval) => {
        if (current.length > 0 && interval.startMinute >= clusterEnd) {
            clusters.push(current);
            current = [];
            clusterEnd = -Infinity;
        }
        current.push(interval);
        clusterEnd = Math.max(clusterEnd, interval.endMinute);
    });
    if (current.length > 0) clusters.push(current);

    const visible: EventLayout[] = [];
    const hidden: HiddenOverlap[] = [];
    clusters.forEach((cluster) => {
        const laneEnds: number[] = [];
        const laidOut = cluster.map((interval) => {
            let lane = laneEnds.findIndex((end) => end <= interval.startMinute);
            if (lane < 0) lane = laneEnds.length;
            laneEnds[lane] = interval.endMinute;
            return { ...interval, lane };
        });
        const laneCount = laneEnds.length;
        laidOut.forEach((item) => {
            if (item.lane < maxVisibleLanes) visible.push({ ...item, laneCount: Math.min(laneCount, maxVisibleLanes) });
        });
        const hiddenItems = laidOut.filter((item) => item.lane >= maxVisibleLanes);
        if (hiddenItems.length > 0) {
            hidden.push({
                count: hiddenItems.length,
                startMinute: Math.min(...hiddenItems.map((item) => item.startMinute)),
                endMinute: Math.max(...laidOut.map((item) => item.endMinute)),
                events: laidOut.map((item) => item.event),
            });
        }
    });

    return { visible, hidden };
};

export const formatPeriodTitle = (period: SmartSchedulePeriod, selectedDay: SmartScheduleDateInput) => {
    const date = toSmartScheduleKst(selectedDay);
    if (period === 'month') return date.format('YYYY년 M월');
    if (period === 'week') {
        const start = startOfMondayWeek(date);
        const end = start.add(6, 'day');
        if (start.month() === end.month()) return `${start.format('YYYY년 M월 D일')} – ${end.format('D일')}`;
        return `${start.format('YYYY년 M월 D일')} – ${end.format('M월 D일')}`;
    }
    return `${date.format('YYYY년 M월 D일')} (${DAY_NAMES[date.day()]})`;
};

export const getDefaultCreateTime = (selectedDay: SmartScheduleDateInput) => {
    const selected = toSmartScheduleKst(selectedDay);
    const now = toSmartScheduleKst(new Date());
    if (!selected.isSame(now, 'day')) return '09:00';
    const roundedMinutes = Math.min(23 * 60 + 30, Math.ceil((now.hour() * 60 + now.minute()) / 30) * 30);
    const hour = Math.floor(roundedMinutes / 60);
    const minute = roundedMinutes % 60;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

export const formatSlotTime = (totalMinutes: number) => {
    const safe = Math.round(Math.max(0, Math.min(24 * 60, totalMinutes)));
    const hour = Math.floor(safe / 60);
    const minute = safe % 60;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

export const getConfirmedCoachCount = (events: SmartScheduleEvent[], coachId: string, date: Dayjs) =>
    events.filter(
        (event) =>
            event.coachId === coachId &&
            event.isConfirmed &&
            (getKindGroup(event) === 'private' || getKindGroup(event) === 'group') &&
            getEventStart(event).isSame(date, 'day')
    ).length;
